/**
 * Finance Ingestion Contract — sole write path for financial events.
 *
 * Producers (invoices, payroll, banking, UI) must call postFinanceEvent /
 * helpers. Never insert finance_movements directly.
 *
 * Client pre-check uses producer posting availability (module enabled), not
 * Finance UI permission (finance_access). Manual Finance UI screens still gate
 * on finance_access separately; RPC enforces allowlisted producer event types.
 */

import { actorCanPostFinanceEvents } from "./nexoraOpsAccess";

export const FINANCE_EVENT_TYPES = Object.freeze([
  "invoice_payment",
  "expense_payment",
  "payroll_disbursement",
  "transfer",
  "interest",
  "fee",
  "refund",
  "adjustment",
  "bank_import_line",
  "opening_balance_seed",
  "manual_inbound",
  "manual_outbound",
]);

export const FINANCE_EVENT_STATUSES = Object.freeze([
  "draft",
  "expected",
  "committed",
  "posted",
  "voided",
]);

export const FINANCE_FLOWS = Object.freeze(["inbound", "outbound"]);

function requireClient(client) {
  if (!client?.rpc) {
    throw new Error("Supabase client with rpc is required.");
  }
}

function normalizeMovement(raw = {}) {
  const accountId = raw.account_id || raw.accountId;
  const flow = String(raw.flow || "").trim();
  const amount = Number(raw.amount);

  if (!accountId) {
    throw new Error("Each movement requires account_id.");
  }
  if (!FINANCE_FLOWS.includes(flow)) {
    throw new Error('Movement flow must be "inbound" or "outbound".');
  }
  if (!Number.isFinite(amount) || amount < 0) {
    throw new Error("Movement amount must be a non-negative number.");
  }

  const movement = {
    account_id: accountId,
    flow,
    amount,
  };

  if (raw.currency) {
    movement.currency = String(raw.currency).trim().toUpperCase();
  }

  return movement;
}

/**
 * Post a financial event atomically via finance_post_event RPC.
 *
 * @returns {{ eventId: string, idempotentReplay: boolean, raw: object }}
 */
export async function postFinanceEvent(client, payload = {}) {
  requireClient(client);

  const allowed = await actorCanPostFinanceEvents(client);
  if (!allowed) {
    throw new Error("Finance module is not available for this organization.");
  }

  const organizationId = payload.organization_id || payload.organizationId;
  const eventType = payload.event_type || payload.eventType;
  const idempotencyKey = payload.idempotency_key || payload.idempotencyKey;
  const movementsInput = payload.movements;

  if (!organizationId) {
    throw new Error("organization_id is required.");
  }
  if (!eventType || !FINANCE_EVENT_TYPES.includes(eventType)) {
    throw new Error("A valid event_type is required.");
  }
  if (!idempotencyKey || !String(idempotencyKey).trim()) {
    throw new Error("idempotency_key is required.");
  }
  if (!Array.isArray(movementsInput) || movementsInput.length < 1) {
    throw new Error("At least one movement is required.");
  }

  const status = payload.status || "posted";
  if (!FINANCE_EVENT_STATUSES.includes(status)) {
    throw new Error("Invalid event status.");
  }

  const movements = movementsInput.map(normalizeMovement);

  if (eventType === "transfer" && movements.length !== 2) {
    throw new Error("Transfers require exactly two movements.");
  }

  const rpcPayload = {
    organization_id: organizationId,
    event_type: eventType,
    status,
    occurred_on: payload.occurred_on || payload.occurredOn || undefined,
    idempotency_key: String(idempotencyKey).trim(),
    source_system: payload.source_system || payload.sourceSystem || "manual",
    description: payload.description || null,
    notes: payload.notes || null,
    category_id: payload.category_id || payload.categoryId || null,
    source_type: payload.source_type || payload.sourceType || null,
    source_id: payload.source_id || payload.sourceId || null,
    source_ref: payload.source_ref || payload.sourceRef || null,
    reversal_of_event_id:
      payload.reversal_of_event_id || payload.reversalOfEventId || null,
    metadata: payload.metadata || {},
    movements,
  };

  const { data, error } = await client.rpc("finance_post_event", {
    p_payload: rpcPayload,
  });

  if (error) {
    throw error;
  }

  const raw = data && typeof data === "object" ? data : {};
  return {
    eventId: raw.event_id || null,
    idempotentReplay: raw.idempotent_replay === true,
    raw,
  };
}

/** Convenience: money in (single inbound movement). */
export async function createManualInboundEvent(client, input = {}) {
  return postFinanceEvent(client, {
    organization_id: input.organization_id || input.organizationId,
    event_type: "manual_inbound",
    status: input.status || "posted",
    occurred_on: input.occurred_on || input.occurredOn,
    idempotency_key: input.idempotency_key || input.idempotencyKey,
    description: input.description,
    notes: input.notes,
    category_id: input.category_id || input.categoryId,
    source_system: "manual",
    movements: [
      {
        account_id: input.account_id || input.accountId,
        flow: "inbound",
        amount: input.amount,
        currency: input.currency,
      },
    ],
  });
}

/** Convenience: money out (single outbound movement). */
export async function createManualOutboundEvent(client, input = {}) {
  return postFinanceEvent(client, {
    organization_id: input.organization_id || input.organizationId,
    event_type: "manual_outbound",
    status: input.status || "posted",
    occurred_on: input.occurred_on || input.occurredOn,
    idempotency_key: input.idempotency_key || input.idempotencyKey,
    description: input.description,
    notes: input.notes,
    category_id: input.category_id || input.categoryId,
    source_system: "manual",
    movements: [
      {
        account_id: input.account_id || input.accountId,
        flow: "outbound",
        amount: input.amount,
        currency: input.currency,
      },
    ],
  });
}

/** Invoice paid → inbound cash (producer: invoicing). */
export async function createInvoicePaymentEvent(client, input = {}) {
  return postFinanceEvent(client, {
    organization_id: input.organization_id || input.organizationId,
    event_type: "invoice_payment",
    status: input.status || "posted",
    occurred_on: input.occurred_on || input.occurredOn,
    idempotency_key: input.idempotency_key || input.idempotencyKey,
    description: input.description,
    notes: input.notes,
    category_id: input.category_id || input.categoryId,
    // DB check finance_events_source_system_valid uses plural "invoices".
    source_system: input.source_system || input.sourceSystem || "invoices",
    source_type: input.source_type || input.sourceType || "invoice",
    source_id: input.source_id || input.sourceId,
    source_ref: input.source_ref || input.sourceRef,
    metadata: input.metadata || {},
    movements: [
      {
        account_id: input.account_id || input.accountId,
        flow: "inbound",
        amount: input.amount,
        currency: input.currency,
      },
    ],
  });
}

/**
 * Invoice payment reversal → outbound cash undoing a prior invoice_payment.
 * Uses event_type invoice_payment so producers without finance_access can post.
 */
export async function createInvoicePaymentReversalEvent(client, input = {}) {
  const reversalOf =
    input.reversal_of_event_id || input.reversalOfEventId || null;
  if (!reversalOf) {
    throw new Error("reversal_of_event_id is required to reverse a payment.");
  }

  return postFinanceEvent(client, {
    organization_id: input.organization_id || input.organizationId,
    event_type: "invoice_payment",
    status: input.status || "posted",
    occurred_on: input.occurred_on || input.occurredOn,
    idempotency_key: input.idempotency_key || input.idempotencyKey,
    description: input.description,
    notes: input.notes,
    category_id: input.category_id || input.categoryId,
    source_system: input.source_system || input.sourceSystem || "invoices",
    source_type: input.source_type || input.sourceType || "invoice",
    source_id: input.source_id || input.sourceId,
    source_ref: input.source_ref || input.sourceRef,
    reversal_of_event_id: reversalOf,
    metadata: {
      action: "payment_reversal",
      ...(input.metadata || {}),
    },
    movements: [
      {
        account_id: input.account_id || input.accountId,
        flow: "outbound",
        amount: input.amount,
        currency: input.currency,
      },
    ],
  });
}

/** Convenience: transfer between two accounts. */
export async function createTransferEvent(client, input = {}) {
  const amount = input.amount;
  return postFinanceEvent(client, {
    organization_id: input.organization_id || input.organizationId,
    event_type: "transfer",
    status: input.status || "posted",
    occurred_on: input.occurred_on || input.occurredOn,
    idempotency_key: input.idempotency_key || input.idempotencyKey,
    description: input.description || "Transfer",
    notes: input.notes,
    source_system: "manual",
    movements: [
      {
        account_id: input.from_account_id || input.fromAccountId,
        flow: "outbound",
        amount,
        currency: input.currency,
      },
      {
        account_id: input.to_account_id || input.toAccountId,
        flow: "inbound",
        amount,
        currency: input.currency,
      },
    ],
  });
}
